import { useI18n } from 'vue-i18n'

interface IField {
  value: string
  isInteracted: boolean
  isDirty: boolean
}

interface IValidationRule {
  pattern?: RegExp
  minLength?: number
  maxLength?: number
  matchField?: string
  errorMessage: string
}

interface IFormConfig {
  [key: string]: {
    rules?: IValidationRule[]
    emptyMessage?: string
  }
}

interface IValidationResult {
  isValid: boolean
  errorMessage: string
}

interface IValidationResponse {
  validateField: (field: string) => IValidationResult
  markInteracted: (field: string) => void
  markDirty: (field: string) => void
  validateForm: () => boolean
  validateOptionalForm: (requiredFields?: string[]) => boolean
}

type ValidationChecker = (value: string, rule: IValidationRule, form: Record<string, IField>) => boolean

const validationCheckers: Record<string, ValidationChecker> = {
  pattern: (value, rule) => 
    rule.pattern ? rule.pattern.test(value) : true,
  
  minLength: (value, rule) => 
    rule.minLength ? value.length >= rule.minLength : true,
  
  maxLength: (value, rule) => 
    rule.maxLength ? value.length <= rule.maxLength : true,
  
  matchField: (value, rule, form) => 
    rule.matchField ? form[rule.matchField].value === value : true
}

export const useFormValidation = (form: Record<string, IField>, config: IFormConfig): IValidationResponse => {

  const markInteracted = (field: string) => {
    form[field].isInteracted = true
  }

  const markDirty = (field: string) => {
    if (form[field].isInteracted) {
      form[field].isDirty = true
    }
  }

  const validateField = (field: string): IValidationResult => {
    if (!form[field].isInteracted && !form[field].isDirty) {
      return { isValid: true, errorMessage: '' }
    }
    
    if (!form[field].value) {
      return { 
        isValid: false, 
        errorMessage: config[field].emptyMessage ? config[field].emptyMessage : ''
      }
    }

    const fieldRules = config[field]?.rules || []
    
    for (const rule of fieldRules) {
      const isValid = Object.entries(validationCheckers).every(([key, checker]) => 
        checker(form[field].value, rule, form)
      )

      if (!isValid) {
        return { isValid: false, errorMessage: rule.errorMessage }
      }
    }
    
    return { isValid: true, errorMessage: '' }
  }

  const validateForm = () => {
    for (const field of Object.values(form)) {
      if (!field.value) {
        field.isDirty = true
        field.isInteracted = true
        return false
      }
    }

    for (const fieldName of Object.keys(form)) {
      if (!validateField(fieldName).isValid) {
        return false
      }
    }

    return true
  }

  const validateOptionalForm = (requiredFields: string[] = []) => {
    for (const fieldName of requiredFields) {
      const field = form[fieldName]
      if (!field.value) {
        field.isDirty = true
        field.isInteracted = true
        return false
      }
    }

    for (const [fieldName, field] of Object.entries(form)) {
      if (field.value && !validateField(fieldName).isValid) {
        return false
      }
    }

    return true
  }

  return {
    validateField,
    markInteracted,
    markDirty,
    validateForm,
    validateOptionalForm
  }
} 